import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../context/CurrencyContext';
import axios from 'axios';
import CONFIG from '../config';
import { getToken } from '../utils/auth';
import { Link } from 'react-router-dom';

const UserOrders = () => {
    const { t, i18n } = useTranslation();
    const isAr = i18n.language?.startsWith('ar');
    const { formatPrice } = useCurrency();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                const token = getToken();
                const res = await axios.get(`${CONFIG.API_URL}/api/orders/my-orders`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setOrders(res.data);
            } catch (err) {
                console.error('Failed to fetch orders:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchOrders();
    }, []);

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'bg-amber-50 text-amber-600 border-amber-100';
            case 'processing': return 'bg-blue-50 text-blue-600 border-blue-100';
            case 'shipped': return 'bg-indigo-50 text-indigo-600 border-indigo-100';
            case 'delivered': return 'bg-green-50 text-green-600 border-green-100';
            case 'cancelled': return 'bg-red-50 text-red-600 border-red-100';
            default: return 'bg-gray-50 text-gray-600 border-gray-100';
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-gray-100 border-t-brand-primary rounded-full animate-spin" />
            </div>
        );
    }

    if (orders.length === 0) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center p-8 text-center animate-fade-in">
                <div className="w-24 h-24 mb-6 rounded-full bg-gray-50 flex items-center justify-center text-4xl text-gray-200">
                    <i className="fas fa-box-open"></i>
                </div>
                <h2 className="text-3xl font-black text-gray-900 mb-4">{t('orders.empty', 'No orders yet')}</h2>
                <p className="text-gray-500 mb-8 max-w-md">{t('orders.empty_desc', 'When you place an order, it will appear here. Start shopping and fill your home with style.')}</p>
                <Link to="/shop" className="bg-brand-primary text-white px-10 py-4 rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-brand-primary/20 hover:scale-105 active:scale-95 transition-all">
                    {t('orders.start_shopping', 'Start Shopping')}
                </Link>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-20 animate-fade-in">
            <div className="flex flex-col lg:flex-row gap-12">
                {/* Sidebar - Same as Profile */}
                <div className="w-full lg:w-80 space-y-4">
                    <div className="bg-white rounded-3xl border border-gray-100 shadow-xl p-4 space-y-1">
                        <Link to="/profile" className="flex items-center gap-4 p-4 rounded-2xl text-gray-400 hover:bg-gray-50 hover:text-gray-900 transition-all font-black text-sm">
                            <i className="fas fa-user-circle w-6"></i>
                            {t('profile.personal_info', 'Personal Info')}
                        </Link>
                        <Link to="/profile/orders" className="flex items-center gap-4 p-4 rounded-2xl bg-brand-primary text-white shadow-lg shadow-brand-primary/10 transition-all font-black text-sm">
                            <i className="fas fa-shopping-bag w-6"></i>
                            {t('profile.my_orders', 'My Orders')}
                        </Link>
                        <Link to="/wishlist" className="flex items-center gap-4 p-4 rounded-2xl text-gray-400 hover:bg-gray-50 hover:text-gray-900 transition-all font-black text-sm">
                            <i className="fas fa-heart w-6"></i>
                            {t('profile.wishlist_sidebar', 'Wishlist')}
                        </Link>
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 space-y-8">
                    <div className="bg-white rounded-3xl border border-gray-100 shadow-2xl p-10">
                        <div className="flex items-center justify-between mb-10 pb-6 border-b border-gray-50">
                            <h3 className="text-2xl font-black text-gray-900 tracking-tight">{t('orders.title', 'Order History')}</h3>
                            <span className="text-[10px] font-black uppercase tracking-widest text-gray-300">
                                {orders.length} {t('orders.total_orders', 'Total Orders')}
                            </span>
                        </div>

                        <div className="space-y-6">
                            {orders.map((order) => (
                                <div key={order.id} className="group p-6 rounded-3xl border border-gray-50 bg-gray-50/30 hover:bg-white hover:shadow-xl hover:border-white transition-all duration-500">
                                    <div className="flex flex-wrap items-center justify-between gap-6 mb-6">
                                        <div className="flex items-center gap-6">
                                            <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center text-gray-300 text-xl group-hover:bg-brand-primary group-hover:text-white transition-all">
                                                <i className="fas fa-box"></i>
                                            </div>
                                            <div>
                                                <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">{t('orders.order_id', 'Order ID')}</p>
                                                <p className="font-black text-gray-900">#{order.id.split('_').pop().toUpperCase()}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-10">
                                            <div className="hidden sm:block">
                                                <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">{t('orders.date', 'Date')}</p>
                                                <p className="font-bold text-gray-600 text-sm">{new Date(order.created_at).toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : 'en-US')}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">{t('orders.status', 'Status')}</p>
                                                <span className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${getStatusColor(order.status)}`}>
                                                    {t(`orders.status_${order.status}`, order.status)}
                                                </span>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">{t('orders.total', 'Total')}</p>
                                                <p className="font-black text-brand-primary text-xl">{formatPrice({ price: order.total_amount, currency: order.currency })}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="pt-6 border-t border-gray-100 flex items-center justify-between">
                                        <p className="text-xs text-gray-400 font-medium">
                                            <i className="fas fa-map-marker-alt mr-2 text-gray-200"></i>
                                            {order.shipping_address.substring(0, 40)}...
                                        </p>
                                        <Link to={`/profile/orders/${order.id}`} className="text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-brand-primary transition-all flex items-center gap-2">
                                            {t('orders.view_details', 'View Details')}
                                            <i className={`fas fa-chevron-${isAr ? 'left' : 'right'}`}></i>
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserOrders;
